import {
  Container,
  Grid,
  InputLabel,
  Typography,
  MenuItem,
  FormControl,
  Select,
  TextField,
  Button,
  Stack,
  Card,
  Skeleton,
  FormHelperText,
} from '@mui/material';
import { AnyFieldApi, useForm } from '@tanstack/react-form';
import { useGetTeamsQuery } from 'src/api/teams/getTeams';
import { useGetCompetitionsQuery } from 'src/api/competitions/getCompetitions';
import moment from 'moment';
import { useCreateMatchMutation } from 'src/api/matches/createMatch';
import { MatchType, TeamType } from 'src/types/domain.types';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { generateTitle } from './helpers/generateTitle';
import { LanguageTabs } from 'src/components/language-tabs';
import { TabPanel } from 'src/components/language-tabs/TabPanel';
import { visibleLanguages } from 'src/config/quill-text-editor-settings';
import { AnimatedTeamBox } from './AnimatedTeamBox';
import { useGetMatchesQuery } from 'src/api/matches/getMatches';
import { useUpdateMatchMutation } from 'src/api/matches/updateMatch';
import Iconify from 'src/components/iconify';
import { MatchSchema, matchSchema } from './helpers/match.schema';
import { AnimatedList } from 'src/components/AnimatedList';
import { LocalizedDateTimePicker } from 'src/components/form/LocalizedDateTimePicker';

export default function MatchPage() {
  const navigate = useNavigate();
  const matchId = useParams<{ id: string }>().id;

  const { data: matches = [], isLoading } = useGetMatchesQuery();

  const match = matches?.find((match) => match.match_id === Number(matchId));

  const [value, setValue] = useState(0);
  const { data: teamsOptions = [] } = useGetTeamsQuery();
  const {
    data: competitionsOptions = [],
    isLoading: isGetCompetitionsLoading,
  } = useGetCompetitionsQuery();

  const { mutateAsync: createMatch, isPending: isCreateMatchPending } =
    useCreateMatchMutation();
  const { mutateAsync: updateMatch, isPending: isUpdateMatchPending } =
    useUpdateMatchMutation();

  const defaultMatch =
    match ??
    ({
      name_en: '',
      competition_id: 1,
      start_utc: moment().toISOString(),
      match_feed_id: '',
      teams: [],
    } as unknown as MatchSchema);

  const form = useForm({
    defaultValues: defaultMatch,
    onSubmit: async ({ value }) => {
      if (match) {
        await updateMatch(value as MatchType);
        return;
      }
      await createMatch(value as MatchType);
    },
    validators: {
      onChangeAsync: matchSchema,
    },
  });

  useEffect(() => {
    if (competitionsOptions?.length && match?.competition_id) {
      form.setFieldValue('competition_id', match?.competition_id);
    }
  }, [competitionsOptions, match, form]);

  const handleChangeTabs = (_event: any, newValue: any) => {
    setValue(newValue);
  };

  const handleDateChange = (newValue: string) => {
    form.setFieldValue('start_utc', newValue);
  };

  const onTeamsChangeListener = (teams: TeamType[]) => {
    visibleLanguages.forEach((lang) => {
      // if title is not empty, do not update
      if (form.getFieldValue(`name_${lang.code}` as keyof MatchSchema)) {
        return;
      }
      const title = generateTitle(teams, lang.code);
      form.setFieldValue(`name_${lang.code}` as keyof MatchSchema, title);
    });
  };

  return (
    <Container>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          await form.handleSubmit();
        }}
      >
        <Stack
          spacing={2}
          direction='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <Typography variant='h4' gutterBottom>
            {match ? 'Edit Match' : 'Create Match'}
          </Typography>

          <Stack direction='row' spacing={2}>
            <Button
              variant='outlined'
              onClick={() => navigate('/dashboard/matches')}
              startIcon={<Iconify icon='eva:arrow-back-outline' />}
            >
              Back
            </Button>
            <Button
              variant='contained'
              type='submit'
              startIcon={
                <Iconify
                  icon={
                    isCreateMatchPending || isUpdateMatchPending
                      ? 'line-md:loading-loop'
                      : 'eva:save-outline'
                  }
                />
              }
              disabled={!form.state.canSubmit}
            >
              {match ? 'Update' : 'Create'}
            </Button>
          </Stack>
        </Stack>

        <Card sx={{ mt: 2, p: 2 }}>
          {
            <>
              <LanguageTabs value={value} onChange={handleChangeTabs} />
              {visibleLanguages.map((lang, index) => (
                <TabPanel value={value} index={index} key={lang.code}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {isLoading ? (
                        <Skeleton variant='rounded' width='100%' height={56} />
                      ) : (
                        <>
                          {/* @ts-ignore form error */}
                          <form.Field
                            name={`name_${lang.code}` as keyof MatchSchema}
                            children={({ state, handleChange, handleBlur }) => {
                              return (
                                <TextField
                                  required={lang.code === 'en'}
                                  label={`Title ${lang.label}`}
                                  fullWidth
                                  value={state.value}
                                  onChange={(e) => handleChange(e.target.value)}
                                  onBlur={handleBlur}
                                  error={!!state.meta.errors.length}
                                  helperText={state.meta.errors?.[0]?.message}
                                />
                              );
                            }}
                          />
                        </>
                      )}
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} mt={0.5}>
                    <Grid item xs={6}>
                      {isLoading ? (
                        <Skeleton variant='rounded' width='100%' height={56} />
                      ) : (
                        <>
                          {/* @ts-ignore form error */}
                          <form.Field
                            name='start_utc'
                            children={({ state }) => (
                              <LocalizedDateTimePicker
                                label='Start'
                                onChange={handleDateChange}
                                value={state.value}
                              />
                            )}
                          />
                        </>
                      )}
                    </Grid>

                    <Grid item xs={6}>
                      {isLoading ? (
                        <Skeleton variant='rounded' width='100%' height={56} />
                      ) : (
                        <>
                          {/* @ts-ignore form error */}
                          <form.Field
                            name='match_feed_id'
                            children={({
                              state,
                              handleChange,
                              handleBlur,
                            }: AnyFieldApi) => (
                              <TextField
                                required
                                label='Match Feed Id'
                                fullWidth
                                value={state.value}
                                onChange={(e) => handleChange(e.target.value)}
                                onBlur={handleBlur}
                              />
                            )}
                          />
                        </>
                      )}
                    </Grid>

                    <Grid item xs={6}>
                      {isLoading || isGetCompetitionsLoading ? (
                        <Skeleton variant='rounded' width='100%' height={56} />
                      ) : (
                        <>
                          {/* @ts-ignore form error */}
                          <form.Field
                            name='competition_id'
                            children={({ state }) => {
                              return (
                                <FormControl fullWidth>
                                  <InputLabel id='competition_id'>
                                    Competition
                                  </InputLabel>
                                  <Select
                                    label='Competition'
                                    value={state.value}
                                    defaultValue={state.value}
                                    onChange={(e) =>
                                      form.setFieldValue(
                                        'competition_id',
                                        Number(e.target.value)
                                      )
                                    }
                                    error={!!state.meta.errors.length}
                                  >
                                    {competitionsOptions.map((competition) => (
                                      <MenuItem
                                        key={competition.competition_id}
                                        value={competition.competition_id}
                                      >
                                        {competition.name_en}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {state.meta.errors?.[0] && (
                                    <FormHelperText color='error'>
                                      {state.meta.errors?.[0]?.message}
                                    </FormHelperText>
                                  )}
                                </FormControl>
                              );
                            }}
                          />
                        </>
                      )}
                    </Grid>
                  </Grid>

                  {/* Teams section */}
                  <Grid container mt={1}>
                    <Typography variant='h6' gutterBottom>
                      Teams
                    </Typography>
                    <Grid container>
                      <Grid item xs={4}>
                        {/* @ts-ignore form error */}
                        <form.Field
                          name='teams'
                          children={({ state, handleChange, handleBlur }) => (
                            <FormControl fullWidth>
                              <InputLabel id='teams'>Teams</InputLabel>
                              <Select
                                label='Teams'
                                multiple
                                value={
                                  state.value?.map((team) => team.team_id) ?? []
                                }
                                onChange={(event) => {
                                  const selectedTeamIds = event.target
                                    .value as number[];
                                  const selectedTeams =
                                    teamsOptions?.filter((team) =>
                                      selectedTeamIds.includes(team.team_id)
                                    ) || [];
                                  handleChange(selectedTeams);
                                  onTeamsChangeListener(selectedTeams);
                                }}
                                onBlur={handleBlur}
                              >
                                {teamsOptions?.map((team) => (
                                  <MenuItem
                                    key={team.team_id}
                                    value={team.team_id}
                                  >
                                    {team[
                                      `name_${lang.code}` as keyof TeamType
                                    ] ?? team.name_en}{' '}
                                    ({team.team_feed_id})
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        />
                      </Grid>
                    </Grid>

                    <Grid item xs={12} mt={1}>
                      {/* @ts-ignore form error */}
                      <form.Field
                        name='teams'
                        children={({
                          handleChange,
                          state,
                        }: {
                          handleChange: (e: any) => void;
                          state: any;
                        }) => {
                          return (
                            <>
                              <div
                                style={{
                                  backgroundColor: '#f8f9fa',
                                  border: '1px solid #e9ecef',
                                  borderRadius: '12px',
                                  padding: '16px',
                                  minHeight: '200px',
                                }}
                              >
                                {!state.value?.length ? (
                                  <div
                                    style={{
                                      textAlign: 'center',
                                      color: '#666',
                                      padding: '40px',
                                      fontSize: '16px',
                                    }}
                                  >
                                    No teams yet. Add teams to get started! 🚀
                                  </div>
                                ) : (
                                  <AnimatedList
                                    list={state.value}
                                    onDelete={(teamId) => {
                                      const newValue = state.value?.filter(
                                        (t: TeamType) => t.team_id !== teamId
                                      );
                                      handleChange(newValue);
                                      onTeamsChangeListener(newValue);
                                    }}
                                    getItemId={(team: TeamType) => team.team_id}
                                    renderItem={(team) => (
                                      <AnimatedTeamBox
                                        key={String(team.team_id)}
                                        team={team}
                                        form={form}
                                        lang={lang}
                                      />
                                    )}
                                  />
                                )}
                              </div>
                            </>
                          );
                        }}
                      />
                    </Grid>
                  </Grid>
                </TabPanel>
              ))}
            </>
          }
        </Card>
      </form>
    </Container>
  );
}
